import { Box, Breakpoint, SxProps } from "@mui/system";
import { useEffect, useMemo, useRef, useState } from "react";

type ResponsiveValue<T> = {
  [key in Breakpoint]?: T;
};

type AnimationType =
  | "slideBottom"
  | "slideLeft"
  | "slideRight"
  | "slideTop"
  | "fadeIn"
  | "blurIn"
  | "popIn";

type AnimatedViewProps = {
  children: React.ReactNode;
  animation?: AnimationType;
  sx?: SxProps;
  duration?: number | ResponsiveValue<number>;
  delay?: number | ResponsiveValue<number>;
};

export const AnimatedView = ({
  children,
  duration,
  delay = 0,
  sx = {},
  animation = "slideBottom"
}: AnimatedViewProps) => {
  const domRef = useRef<Element>();
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisible(true);

        if (domRef.current instanceof Element) {
          observer.unobserve(domRef.current as Element);
        }
      }
    });

    if (domRef.current instanceof Element) {
      observer.observe(domRef.current as Element);
    }

    return () => observer.disconnect();
  }, []);

  const createResponsiveValue = <T, R>(
    value: ResponsiveValue<T>,
    replace: (value: T) => R
  ): ResponsiveValue<R> => {
    const responsiveValue: ResponsiveValue<R> = {};

    for (const key in value) {
      const breakpoint = key as Breakpoint;
      const val = value[breakpoint];
      if (val !== undefined) {
        responsiveValue[breakpoint] = replace(val);
      }
    }

    return responsiveValue;
  };

  const transitionDelay = useMemo(
    () =>
      typeof delay === "number" || !delay
        ? `${delay || 0}ms`
        : createResponsiveValue(delay as ResponsiveValue<number>, (val) => `${val}ms`),
    [delay]
  );

  const stylesCommon: SxProps = {
    width: "100%"
  };

  const animations: Record<string, SxProps> = useMemo(() => {
    const slideAnimationCommon: SxProps = {
      transition:
        typeof duration === "number" || !duration
          ? `transform ${duration || 300}ms ease-out`
          : createResponsiveValue(
              duration as ResponsiveValue<number>,
              (val) => `transform ${val}ms ease-out`
            ),
      transitionDelay: transitionDelay,
      willChange: "transform"
    };

    return {
      slideBottom: {
        transform: isVisible ? "none" : "translate(0, 10%)",
        ...slideAnimationCommon,
        ...sx
      },
      slideTop: {
        ...slideAnimationCommon,
        transform: isVisible ? "none" : "translate(0, -10%)",
        ...sx
      },
      slideLeft: {
        ...slideAnimationCommon,
        transform: isVisible ? "none" : "translate(-10%, 0)",
        ...sx
      },
      slideRight: {
        ...slideAnimationCommon,
        transform: isVisible ? "none" : "translate(10%, 0)",
        ...sx
      },
      fadeIn: {
        visibility: isVisible ? "visible" : "hidden",
        opacity: isVisible ? 1 : 0,
        transition:
          typeof duration === "number" || !duration
            ? `opacity ${duration || 1000}ms ease-out, visibility ${duration || 1000}ms ease-out`
            : createResponsiveValue(
                duration as ResponsiveValue<number>,
                (val) => `opacity ${val}ms ease-out, visibility ${val}ms ease-out`
              ),
        transitionDelay: transitionDelay,
        willChange: "opacity, visibility",
        ...sx
      },
      blurIn: {
        visibility: isVisible ? "visible" : "hidden",
        filter: isVisible ? "blur(0)" : "blur(20px)",
        transition:
          typeof duration === "number" || !duration
            ? `filter ${duration || 300}ms ease-out`
            : createResponsiveValue(
                duration as ResponsiveValue<number>,
                (val) => `filter ${val}ms ease-out`
              ),
        transitionDelay: transitionDelay,
        willChange: "filter",
        ...sx
      },
      popIn: {
        transform: isVisible ? "scale(1)" : "scale(0.5)",
        transition:
          typeof duration === "number" || !duration
            ? `transform ${duration || 500}ms ease-out`
            : createResponsiveValue(
                duration as ResponsiveValue<number>,
                (val) => `transform ${val}ms ease-out`
              ),
        transitionDelay: transitionDelay,
        willChange: "transform",
        ...sx
      }
    };
  }, [duration, transitionDelay, isVisible, sx]);

  return (
    <Box ref={domRef} sx={{ ...stylesCommon, ...animations[animation] }}>
      {children}
    </Box>
  );
};
